import { type MutableRefObject, type VideoHTMLAttributes, forwardRef, useMemo, useRef } from 'react';
import useIntersectionObserver from '@activebrands/core-web/hooks/useIntersectionObserver';
import { VideoSrcObject } from '@activebrands/core-web/libs/storyblok/storyblok';
import { styled } from '@activebrands/core-web/libs/styletron';
import { StyletronStyle } from '@activebrands/core-web/types/styletron';
import asArray from '@grebban/utils/array/asArray';
import generateRandomString from '@grebban/utils/string/generateRandomString';

const VideoElement = styled('video', {
    width: '100%',
    height: '100%',
});

/**
 * This is a base component of an HTML5 video.
 * @author Hampus Lindholm & Anton Pedersen
 * @version 1.0 (Atomic-css: This style is updated to work with gatsby and styletron.)
 *
 * @param {bool} autoPlay - Should the video autoplay
 * @param {string} className - Used by styled components (Add option to style this component)
 * @param {bool} controls - Option to show or hide video controls (play/pause/mute)
 * @param {bool} loop - Should the video start over or stop
 * @param {bool} muted - Should the video be muted by default
 * @param {bool} playsInline - Option to use playsInline (Important on iOS devices)
 * @param {string|object|string[]|object[]} src - One or serveral video sources as strings or objects
 * @param {bool} poster - The source to the poster (Image shown before video is playing/loaded)
 * @param {string} captions - WebVTT caption file rendered as a <track> element
 */

/* eslint-disable react/require-default-props */
interface VideoProps extends Omit<VideoHTMLAttributes<HTMLVideoElement>, 'loading' | 'src'> {
    $style?: StyletronStyle;
    autoPlay?: boolean;
    captions?: string;
    className?: string;
    controls?: boolean;
    crossOrigin?: VideoHTMLAttributes<HTMLVideoElement>['crossOrigin'];
    loading?: 'auto' | 'lazy' | 'eager';
    loop?: boolean;
    media?: string;
    muted?: boolean;
    playsInline?: boolean;
    poster?: string;
    src?: string | VideoSrcObject | Array<string | VideoSrcObject>;
}

const Video = forwardRef<HTMLVideoElement, VideoProps>(
    (
        {
            autoPlay = false,
            captions,
            className = '',
            controls = false,
            loading = 'eager',
            loop = false,
            muted = true,
            playsInline = false,
            poster = '',
            src,
            ...rest
        }: VideoProps,
        ref
    ) => {
        const isLazy = loading === 'lazy';
        const stringified = JSON.stringify(src);
        // HTML5 video has problems rendering when src is replaced
        // Create a random number used in key to force rerenders
        const internalRef = useRef<HTMLVideoElement | null>(null);
        const randomVideoID = useMemo(() => generateRandomString(9), [stringified]);

        const [observe, unobserve] = useIntersectionObserver(
            entry => {
                if (entry.intersectionRatio > 0 || entry.isIntersecting) {
                    const video = entry.target as HTMLVideoElement;

                    if (video.childElementCount === 0 && !video.src && video.dataset.src) {
                        video.src = video.dataset.src;

                        requestAnimationFrame(() => delete video.dataset.src);
                    } else {
                        const mediaQuery = (video.childNodes[0] as HTMLSourceElement).getAttribute('media');

                        // Update video src if the element doesn't have a mediaQuery or if mediaQuery matches and the element is missing a src
                        if (!mediaQuery || window.matchMedia(mediaQuery).matches) {
                            // set 'src' attribute for all video element source children
                            // this is okay since the browser will only load the first source that it supports
                            for (let i = 0; i < video.childNodes.length; i++) {
                                const source = video.childNodes[i] as HTMLSourceElement;
                                if (!source.src && source.dataset.src) {
                                    source.src = source.dataset.src;
                                    requestAnimationFrame(() => delete source.dataset.src);
                                }
                            }
                        }
                    }

                    video.load();
                    // video.play();

                    unobserve();
                }
            },
            { rootMargin: '200px' }
        );

        const sources = useMemo(
            () =>
                asArray(src).map(source => ({
                    key: source.src || source,
                    type: source.type || 'video/mp4',
                    [`${isLazy ? 'data-' : ''}src`]: source.src || source,
                    media: source.media,
                })),
            [isLazy, stringified]
        );

        const setRef = (node: HTMLVideoElement | null) => {
            if (node && node !== internalRef.current) {
                internalRef.current = node;
                if (typeof ref === 'function') {
                    ref(node);
                } else if (ref) {
                    (ref as MutableRefObject<HTMLVideoElement | null>).current = node;
                }
                isLazy && observe(node);
            }
        };

        const { crossOrigin, ...videoProps } = rest;

        return (
            <VideoElement
                autoPlay={autoPlay}
                className={className}
                controls={controls}
                crossOrigin={captions ? 'anonymous' : crossOrigin}
                key={`${randomVideoID}_${src}`}
                loop={loop}
                muted={muted}
                playsInline={playsInline}
                poster={poster}
                ref={setRef}
                {...videoProps}
            >
                {sources.map(({ key, ...props }) => (
                    <source key={key} {...props} />
                ))}
                {captions && <track default kind="captions" label="Captions" src={captions} />}
            </VideoElement>
        );
    }
);

export default Video;
